import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '../hooks/use-scroll-reveal';
import heroImage from '../assets/hero-ai-transition.jpg';
import stallBg from '../assets/ai-stall-bg.jpg';
import workBg from '../assets/ai-work-bg.jpg';
import ctaBg from '../assets/ai-cta-bg.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-4 mb-8">
    <span className="h-px w-12 bg-primary" aria-hidden="true" />
    <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
      {children}
    </p>
  </div>
);

const Hero = () => {
  const content = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <>
    {/* Hero — magazine cover: one big thesis, quiet frame */}
    <section className="relative overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          className="w-full h-full object-cover object-center opacity-30"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/80 to-foreground" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 12% 85%, hsl(var(--primary) / 0.25) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto pt-20 pb-20 sm:pt-32 sm:pb-40">
        {/* Top rail — magazine masthead */}
        <div className="flex items-center justify-between mb-16 sm:mb-28 text-background/60 text-[0.7rem] tracking-[0.3em] uppercase font-semibold">
          <div className="flex items-center gap-4">
            <span className="h-px w-8 sm:w-12 bg-primary" aria-hidden="true" />
            <span className="text-primary">AI Transformation</span>
          </div>
          <span className="hidden sm:inline">The Human Side</span>
        </div>

        <div
          ref={content.ref}
          className={`transition-all duration-1000 ${
            content.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h1
            className="text-background text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-10 sm:mb-14 max-w-5xl"
            style={{ textWrap: 'balance' }}
          >
            Only a person can be{' '}
            <span className="text-primary italic">trustworthy.</span>
          </h1>

          {/* Pull-quote treatment for the subhead */}
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
            <blockquote className="md:col-span-8">
              <p className="text-background text-xl sm:text-2xl lg:text-3xl leading-[1.3] font-display">
                A model can be reliable.{' '}
                <span className="text-background/70">
                  A human stands behind what it does.
                </span>
              </p>
            </blockquote>
            <div className="md:col-span-4 md:pl-6 md:border-l md:border-background/15">
              <p className="text-background/50 text-xs tracking-[0.2em] uppercase font-semibold mb-2">
                What follows
              </p>
              <p className="text-background/75 text-sm leading-relaxed">
                A threefold call, a stalled deployment, and the two loops that keep the investment alive.
              </p>
            </div>
          </div>
        </div>
      </div>

    </section>

    {/* Threefold — triptych contained within the main page margins */}
    <section
      id="threefold-call"
      ref={tail.ref}
      className={`relative bg-background transition-all duration-1000 ${
        tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="container-mobile max-w-7xl mx-auto pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <h2
            className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
            style={{ textWrap: 'balance' }}
          >
            Every leader now has the same{' '}
            <span className="text-primary italic">choices</span> to make, over and over.
          </h2>
          <p className="mt-5 text-foreground/70 text-base sm:text-lg leading-relaxed max-w-2xl">
            Three <span className="text-primary italic">choices</span>. Made a hundred times a week. This is where the transformation{' '}
            <span className="whitespace-nowrap">happens.</span>
          </p>
        </div>

        {/* Triptych — contained, not full-bleed */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 md:min-h-[26rem] rounded-2xl overflow-hidden shadow-xl">
          {/* Panel 01 — Delegate to people */}
          <article
            className="relative p-8 sm:p-10 md:p-12 flex flex-col justify-between overflow-hidden rounded-t-2xl md:rounded-none md:rounded-l-2xl"
            style={{
              background:
                'linear-gradient(135deg, hsl(var(--primary) / 0.12) 0%, hsl(var(--secondary)) 55%, hsl(var(--primary) / 0.08) 100%)',
            }}
          >
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, hsl(var(--primary)) 0%, transparent 40%), radial-gradient(circle at 80% 90%, hsl(var(--primary)) 0%, transparent 45%)',
              }}
            />
            <div className="relative">
              <span className="text-primary font-semibold tracking-[0.3em] uppercase text-[0.7rem] block mb-8">
                For your people
              </span>
              <p className="text-foreground font-display leading-[1.05]"
                 style={{ fontSize: 'clamp(2rem, 3vw, 2.5rem)' }}>
                Delegate to <span className="text-primary italic">people</span>.
              </p>
            </div>
            <p className="relative mt-8 text-foreground/80 text-base sm:text-lg leading-relaxed">
              So they grow into decisions they could not make yesterday.
            </p>
            <div
              aria-hidden="true"
              className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full opacity-50 pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, hsl(var(--primary) / 0.28) 0%, transparent 70%)',
              }}
            />
          </article>

          {/* Panel 02 — Delegate to machines */}
          <article className="relative bg-foreground p-8 sm:p-10 md:p-12 flex flex-col justify-between overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(hsl(var(--background)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--background)) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
              }}
            />
            <div className="relative">
              <span className="text-primary font-semibold tracking-[0.3em] uppercase text-[0.7rem] block mb-8">
                For the system
              </span>
              <p className="text-background font-display leading-[1.05]"
                 style={{ fontSize: 'clamp(2rem, 3vw, 2.5rem)' }}>
                Delegate to <span className="text-primary italic">machines</span>.
              </p>
            </div>
            <p className="relative mt-8 text-background/75 text-base sm:text-lg leading-relaxed">
              So the work flows without a person in every loop.
            </p>
          </article>

          {/* Panel 03 — Keep human */}
          <article
            className="relative p-8 sm:p-10 md:p-12 flex flex-col justify-between overflow-hidden rounded-b-2xl md:rounded-none md:rounded-r-2xl"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
          >
            <div>
              <span className="text-background font-semibold tracking-[0.3em] uppercase text-[0.7rem] block mb-8">
                For you
              </span>
              <p className="text-background font-display leading-[1.05]"
                 style={{ fontSize: 'clamp(2.25rem, 3.2vw, 2.75rem)' }}>
                Keep <span className="italic text-foreground">human</span>.
              </p>
            </div>
            <div className="mt-8">
              <p className="text-background text-base sm:text-lg leading-relaxed font-medium">
                What only a person can hold.
              </p>
              <p className="mt-2 text-background/80 text-sm sm:text-base leading-relaxed">
                Judgment. Trust. Accountability.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
    </>
  );
};

const TheStall = () => {
  const stat = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-16 sm:py-36 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-40 pointer-events-none"
        style={{ backgroundImage: `url(${stallBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background pointer-events-none"
      />
      <div className="container-mobile max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div
            ref={stat.ref}
            className={`md:col-span-5 transition-all duration-1000 ${
              stat.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Eyebrow>The Stall</Eyebrow>
            <div className="relative">
              <p
                className="font-display text-foreground tracking-tight leading-[1] mb-8"
                style={{ fontSize: 'clamp(5rem, 12vw, 8rem)' }}
              >
                95<span className="text-primary align-super text-[0.4em] ml-1">%</span>
              </p>
              <span className="block h-px w-16 bg-primary mb-6" aria-hidden="true" />
              <p className="text-foreground/85 text-sm sm:text-base tracking-[0.12em] uppercase leading-relaxed max-w-[30ch] md:max-w-[22ch]">
                of enterprise AI initiatives fail to reach production value
              </p>
              <p className="mt-5 text-foreground/55 text-xs leading-relaxed max-w-[34ch]">
                Source: MIT NANDA,{" "}
                <em>The GenAI Divide: State of AI in Business 2025</em>, reported by{" "}
                <a
                  href="https://fortune.com/2025/08/18/mit-report-95-percent-generative-ai-pilots-at-companies-failing-cfo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-primary transition-colors"
                >
                  Fortune, Aug 2025
                </a>
                . Still true in 2026:{" "}
                <a
                  href="https://hbr.org/2026/02/why-ai-adoption-stalls-according-to-industry-data"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-primary transition-colors"
                >
                  Harvard Business Review, Feb 2026
                </a>
                .
              </p>
            </div>
          </div>
          <div
            ref={body.ref}
            className={`md:col-span-7 md:pl-10 md:border-l md:border-border transition-all duration-1000 delay-150 ${
              body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2
              className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-8"
              style={{ textWrap: 'balance' }}
            >
              Deployment dies at <span className="text-primary italic">adoption.</span>
            </h2>
            <div className="space-y-6 text-lg sm:text-xl text-foreground/90 leading-relaxed">
              <p>
                The workflows get mapped. The agents get built. Then the initiative quietly stalls where the human beings are. The person asked to hand twenty years of judgment to a system she suspects will replace her.
              </p>
              <p>
                These moments never appear on the technical plan. They decide whether the system ever gets used.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TheWork = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const loopOne = useScrollReveal<HTMLDivElement>();
  const loopTwo = useScrollReveal<HTMLDivElement>();
  const footer = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-primary py-16 sm:py-36 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: `url(${workBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--background) / 0.14) 0%, transparent 50%)',
        }}
      />
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`grid md:grid-cols-12 gap-8 md:gap-12 mb-16 sm:mb-24 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-background" aria-hidden="true" />
              <p className="text-background font-semibold tracking-[0.25em] uppercase text-xs">
                The Work
              </p>
            </div>
            <h2
              className="text-background text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
              style={{ textWrap: 'balance' }}
            >
              One engagement. <span className="italic text-foreground">Two loops.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-2">
            <p className="text-background/90 text-lg leading-relaxed">
              AI transformation runs on two loops at once. Most engagements only build the first. We work both. That is how the investment survives contact with your people.
            </p>
            <p className="mt-5 text-background/80 text-base sm:text-lg leading-relaxed">
              The industry put a human in the machine loop to extract technical capability. We build the human loop to develop the capacities everyone names as scarce and no one grows: judgment, trust, and the accountability only a person can carry.
            </p>
          </div>
        </div>

        {/* Diptych — two distinct cards */}
        <div
          ref={loopOne.ref}
          className={`grid md:grid-cols-2 gap-6 transition-all duration-1000 delay-100 ${
            loopOne.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Loop — Deployment (light card) */}
          <article className="relative p-8 sm:p-12 md:p-14 flex flex-col rounded-2xl bg-background text-foreground shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              <span className="text-primary font-semibold tracking-[0.3em] uppercase text-[0.7rem]">
                The Machine Loop
              </span>
            </div>
            <p
              className="font-display text-foreground leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)' }}
            >
              Where <span className="text-primary italic">capability</span> scales.
            </p>
            <p className="text-foreground/85 text-base sm:text-lg leading-relaxed mb-8">
              The workflows, decisions, and interfaces where AI creates real leverage. We map where the machine belongs, and where it does not.
            </p>
            <div className="mt-auto pt-6 border-t border-border/60">
              <p className="text-foreground/60 text-[0.7rem] tracking-[0.2em] uppercase font-semibold mb-2">
                Built with partners
              </p>
              <p className="text-foreground/85 leading-relaxed">
                Alongside your technical team, or with builders we trust.
              </p>
            </div>
          </article>

          {/* Loop — Development (dark card) */}
          <article
            ref={loopTwo.ref}
            className="relative p-8 sm:p-12 md:p-14 flex flex-col rounded-2xl bg-foreground text-background shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              <span className="text-primary font-semibold tracking-[0.3em] uppercase text-[0.7rem]">
                The Human Loop
              </span>
            </div>
            <p
              className="font-display text-background leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)' }}
            >
              Where <span className="text-primary italic">capacity</span> grows.
            </p>
            <p className="text-background/85 text-base sm:text-lg leading-relaxed mb-8">
              The identity, trust, and capacity work that decides whether the system ever gets used. This is where the investment survives contact with your people.
            </p>
            <div className="mt-auto pt-6 border-t border-background/20">
              <p className="text-background/60 text-[0.7rem] tracking-[0.2em] uppercase font-semibold mb-2">
                Development inside deployment
              </p>
              <p className="text-background/85 leading-relaxed">
                Deliberate practices threaded into the rollout, while it goes live.
              </p>
            </div>
          </article>
        </div>

        {/* Footnote to the essay */}
        <div
          ref={footer.ref}
          className={`mt-14 sm:mt-20 pt-6 border-t border-background/20 transition-all duration-1000 delay-300 ${
            footer.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-background/80 text-sm sm:text-base leading-relaxed">
            Read the essay that names this idea:{" "}
            <Link
              to="/essays/the-human-loop"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-1 font-display text-background underline underline-offset-4 hover:text-background/90 transition-colors"
            >
              The Human Loop
              <ArrowRight className="h-3 w-3" />
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-16 sm:py-40 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-40 pointer-events-none"
        style={{ backgroundImage: `url(${ctaBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-foreground/70 pointer-events-none"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal max-w-xl mx-auto">
          One conversation is where it begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const AITransition = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI Transformation: The Human Side of AI Deployment',
      provider: { '@type': 'Organization', name: 'ParadoxEdge', url: 'https://www.paradoxedge.com' },
      serviceType: ['AI Transformation', 'AI Adoption', 'Change Management', 'Leadership Development', 'Organizational Development'],
      description:
        'The human side of AI deployment. Dual-loop audits, development inside deployment, and judgment for the space between people and machines.',
      url: 'https://www.paradoxedge.com/ai-transformation',
      areaServed: 'Worldwide',
      audience: { '@type': 'BusinessAudience', audienceType: 'Companies bringing AI into their work' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.paradoxedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'AI Transformation', item: 'https://www.paradoxedge.com/ai-transformation' },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="AI Transformation | The Human Side of AI Deployment | ParadoxEdge"
        description="Most AI initiatives stall at adoption, where the human beings are. ParadoxEdge works the human side of AI deployment so the investment survives contact with your people."
        canonicalUrl="/ai-transformation"
        keywords="AI adoption, AI deployment, change management, human side of AI, AI transformation, leadership development"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />
        <main>
          <Hero />
          <TheStall />
          <TheWork />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AITransition;